/**
 * packages/core/src/v.ts
 *
 * Convex-style schema validator for API arguments.
 * Provides runtime validation, TypeScript inference, and Standard Schema v1
 * (`~standard.validate`) for procedure.query / procedure.mutation.
 */

import type { StandardSchemaV1 } from "@standard-schema/spec";

/**
 * Validation-specific error — thrown by parseArgs on invalid input.
 * Allows the server dispatcher to safely distinguish 400 from 500
 * without fragile string-matching on the error message.
 */
export class GencowValidationError extends Error {
  readonly statusCode = 400;
  constructor(message: string) {
    super(message);
    this.name = "GencowValidationError";
  }
}

/** Resolves unwrapped field schema for `v.object` JSON Schema and `required` keys. */
const GENCOW_OPTIONAL_INNER = Symbol.for("gencow.v.optionalInner");

export type Validator<T = any> = {
  parse(val: any): T;
  _type: T; // Ghost property for TypeScript inference
} & StandardSchemaV1<T, T>;

export type Infer<T> = T extends Validator<infer U> ? U : never;

/** `~standard.jsonSchema` is used by codegen (`schemaToTypeLiteral`); not part of base Standard Schema props typing. */
type GencowStandardProps<T> = StandardSchemaV1.Props<T, T> & { jsonSchema?: unknown };

function assertStandardResult<T>(value: T): StandardSchemaV1.Result<T> {
  return { value } as StandardSchemaV1.Result<T>;
}

function failStandardResult<T>(message: string): StandardSchemaV1.Result<T> {
  return { issues: [{ message }] } as StandardSchemaV1.Result<T>;
}

function readJsonSchemaFragment(validator: Validator<any>): unknown {
  const opt = (validator as { [GENCOW_OPTIONAL_INNER]?: Validator<any> })[GENCOW_OPTIONAL_INNER];
  const target = opt ?? validator;
  const raw = (target["~standard"] as GencowStandardProps<any> | undefined)?.jsonSchema;
  if (raw === undefined) return {};
  return typeof raw === "function" ? (raw as () => unknown)() : raw;
}

function matchesMimeType(fileType: string, pattern: string): boolean {
  const normalizedFileType = fileType.split(";")[0]!.trim().toLowerCase();
  const normalizedPattern = pattern.trim().toLowerCase();
  if (normalizedPattern === "*/*") return true;
  if (normalizedPattern.endsWith("/*")) {
    const prefix = normalizedPattern.slice(0, -1);
    return normalizedFileType.startsWith(prefix);
  }
  return normalizedFileType === normalizedPattern;
}

function buildObjectJsonSchema(fields: Record<string, Validator<any>>): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  for (const key of Object.keys(fields)) {
    const field = fields[key];
    properties[key] = readJsonSchemaFragment(field);
    if (!Object.prototype.hasOwnProperty.call(field, GENCOW_OPTIONAL_INNER)) {
      required.push(key);
    }
  }
  return {
    type: "object",
    properties,
    ...(required.length > 0 ? { required } : {}),
  };
}

function createValidator<T>(config: {
  parse: (val: any) => T;
  _type: T;
  jsonSchema?: unknown;
}): Validator<T> {
  const { parse, _type, jsonSchema } = config;
  const standard: GencowStandardProps<T> = {
    version: 1 as const,
    vendor: "gencow",
    types: {} as { input: T; output: T },
    validate: async (value: unknown) => {
      try {
        return assertStandardResult(parse(value));
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : "Validation failed";
        return failStandardResult<T>(message);
      }
    },
    ...(jsonSchema !== undefined ? { jsonSchema } : {}),
  };
  return {
    parse,
    _type,
    "~standard": standard as StandardSchemaV1.Props<T, T>,
  } as Validator<T>;
}

export const v = {
  string(): Validator<string> {
    return createValidator({
      parse: (val) => {
        if (typeof val !== "string") throw new Error(`Expected string, got ${typeof val}`);
        return val;
      },
      _type: "" as string,
      jsonSchema: { type: "string" },
    });
  },
  number(): Validator<number> {
    return createValidator({
      parse: (val) => {
        // Accept numeric strings (e.g. from URL params / form data)
        const n = typeof val === "string" ? Number(val) : val;
        if (typeof n !== "number" || isNaN(n)) throw new Error(`Expected number, got ${typeof val}`);
        return n;
      },
      _type: 0 as number,
      jsonSchema: { type: "number" },
    });
  },
  boolean(): Validator<boolean> {
    return createValidator({
      parse: (val) => {
        if (typeof val !== "boolean") throw new Error(`Expected boolean, got ${typeof val}`);
        return val;
      },
      _type: false as boolean,
      jsonSchema: { type: "boolean" },
    });
  },
  /** ISO / `Date` — used for Drizzle `timestamp` columns; codegen maps to `Date`. */
  dateTime(): Validator<Date> {
    return createValidator({
      parse: (val) => {
        if (val instanceof Date && !Number.isNaN(val.getTime())) return val;
        if (typeof val === "string") {
          const d = new Date(val);
          if (!Number.isNaN(d.getTime())) return d;
        }
        if (typeof val === "number" && Number.isFinite(val)) {
          const d = new Date(val);
          if (!Number.isNaN(d.getTime())) return d;
        }
        throw new Error(`Expected date, got ${typeof val}`);
      },
      _type: new Date(0),
      jsonSchema: { type: "string", format: "date-time" },
    });
  },
  any(): Validator<any> {
    return createValidator({
      parse: (val) => val,
      _type: null as any,
    });
  },
  optional<T>(inner: Validator<T>): Validator<T | undefined> {
    const out = createValidator<T | undefined>({
      parse: (val) => {
        if (val === undefined || val === null) return undefined;
        return inner.parse(val);
      },
      _type: undefined as T | undefined,
      jsonSchema: readJsonSchemaFragment(inner) as object,
    });
    (out as { [GENCOW_OPTIONAL_INNER]?: Validator<T> })[GENCOW_OPTIONAL_INNER] = inner;
    return out;
  },
  nullable<T>(inner: Validator<T>): Validator<T | null> {
    return createValidator<T | null>({
      parse: (val) => {
        if (val === null) return null;
        return inner.parse(val);
      },
      _type: null as T | null,
      jsonSchema: { anyOf: [readJsonSchemaFragment(inner), { type: "null" }] },
    });
  },
  object<T extends Record<string, Validator>>(fields: T): Validator<{ [K in keyof T]: Infer<T[K]> }> {
    return createValidator({
      parse: (val) => {
        if (typeof val !== "object" || val === null) throw new Error("Expected object");
        const result: any = {};
        for (const key in fields) {
          result[key] = fields[key].parse((val as any)[key]);
        }
        return result;
      },
      _type: {} as { [K in keyof T]: Infer<T[K]> },
      jsonSchema: buildObjectJsonSchema(fields as Record<string, Validator<any>>),
    });
  },
  array<T>(inner: Validator<T>): Validator<T[]> {
    const items = readJsonSchemaFragment(inner);
    return createValidator({
      parse: (val) => {
        if (!Array.isArray(val)) throw new Error("Expected array");
        return val.map((item) => inner.parse(item));
      },
      _type: [] as T[],
      jsonSchema: { type: "array", items },
    });
  },
  /** Browser `File` / `Blob` for procedure multipart uploads. */
  file(): Validator<Blob> & { mime(mimeType: string): Validator<Blob> } {
    const base = createValidator({
      parse: (val) => {
        if (!(val instanceof Blob)) {
          throw new Error(`Expected file, got ${typeof val}`);
        }
        return val;
      },
      _type: null as unknown as Blob,
      jsonSchema: { type: "string", format: "binary", "x-gencow-type": "file" },
    });
    return Object.assign(base, {
      mime(mimeType: string) {
        return createValidator({
          parse: (val) => {
            const blob = base.parse(val);
            if (!matchesMimeType(blob.type, mimeType)) {
              throw new Error(
                `Expected a file of type ${mimeType} but got ${blob.type || "unknown"}`,
              );
            }
            return blob;
          },
          _type: null as unknown as Blob,
          jsonSchema: {
            type: "string",
            format: "binary",
            "x-gencow-type": "file",
            "x-gencow-mime": mimeType,
          },
        });
      },
    });
  },
};

/**
 * Infer TypeScript type from a validator schema or a shorthand record of validators.
 * Correctly handles optional properties (Validator<T | undefined> → optional key).
 */
export type InferArgs<T> =
  T extends Validator<infer U>
    ? U
    : T extends Record<string, Validator>
      ? {
          [K in keyof T as T[K] extends Validator<infer U>
            ? undefined extends U
              ? never
              : K
            : K]: T[K] extends Validator<infer U> ? U : any;
        } & {
          [K in keyof T as T[K] extends Validator<infer U>
            ? undefined extends U
              ? K
              : never
            : never]?: T[K] extends Validator<infer U> ? U : any;
        }
      : T;

/**
 * Validates and parses arguments against a schema at runtime.
 *
 * Supports:
 *   - Full validators: `v.object({ id: v.number() })`, `v.string()`, `v.any()`, etc.
 *   - Shorthand record: `{ id: v.number(), title: v.string() }` (Convex style)
 *
 * Throws `GencowValidationError` (HTTP 400) on validation failure.
 */
export function parseArgs(schema: any, args: any): any {
  if (!schema) return args;

  // Direct Validator (has a .parse method)
  if (typeof schema.parse === "function") {
    try {
      return schema.parse(args);
    } catch (e: any) {
      throw new GencowValidationError(e.message);
    }
  }

  // Shorthand object — e.g. { id: v.number(), title: v.optional(v.string()) }
  if (typeof schema === "object" && schema !== null) {
    // Empty schema {} → passthrough all args (e.g. FormData with file field)
    const schemaKeys = Object.keys(schema);
    if (schemaKeys.length === 0) return args;

    if (typeof args !== "object" || args === null) {
      throw new GencowValidationError("Expected an object for arguments");
    }
    const result: any = {};
    for (const key of schemaKeys) {
      const validator = schema[key];
      if (validator && typeof validator.parse === "function") {
        try {
          result[key] = validator.parse(args[key]);
        } catch (e: any) {
          throw new GencowValidationError(`Argument "${key}": ${e.message}`);
        }
      } else {
        result[key] = args[key];
      }
    }
    return result;
  }

  return args;
}
